import React, {
  useCallback, useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {hooks} from 'farmx-redux-core';
import { RAIN_RATE } from 'components/hooks/graph';
import { convertLengthUnits, getYminMax }from '../../helper/graphHelper';
import NewSensorDataChart from './NewSensorDataChart';
import { VAR_RAIN_RATE } from './constants';

interface Sensor {
  name?: string;
  id?: number;
  type?: string;
  identifier?: string;
}

interface RainRateChartProps {
  sensor?: Sensor | any;
  startDate?: any;
  endDate?: any;
  cached?: string | any;
  updateYminmax?: () => void;
  uniformYAxis?: boolean;
  yMinMaxMap?: any;
}

const { useUnits } = hooks;
const RainRateChart: React.FC<RainRateChartProps> = (props: any) => {
  const {
    sensor, startDate, endDate, cached, updateYminmax, uniformYAxis, yMinMaxMap
  } = props;

  const { t } = useTranslation();
  const [label, setLabel] = useState('');
  const getUserUnits = useUnits();

  const globalYMinMax = yMinMaxMap && yMinMaxMap[RAIN_RATE]
    ?yMinMaxMap[RAIN_RATE]:{min: null, max: null};

  const yMinMax = useMemo(() => {
    if((globalYMinMax.min || globalYMinMax.min===0)
        && globalYMinMax.max) { return { min: globalYMinMax.min, max: globalYMinMax.max}; }
    return null;
  }, [globalYMinMax.min, globalYMinMax.max]);

  const customConfig = useCallback((configObj, seriesData) => {
    if (configObj?.series?.[0]){
      const yMinMax = getYminMax(seriesData) || { min: 0, max: 0};
      const { multiplier } = configObj?.series?.[0] || { multiplier: 1 };
      updateYminmax(RAIN_RATE, yMinMax.min * multiplier, yMinMax.max * multiplier);
      const { units } = seriesData?.rain_rate?.[0];
      const dataUnit = units || 'millimeters';
      const rainRateData = configObj?.series?.[0].data;
      const data = convertLengthUnits(rainRateData, getUserUnits, dataUnit);
      const {value: yMin, label} = getUserUnits(configObj?.yAxis?.[0]?.min
         || yMinMax?.min, dataUnit, 'depth', {decimalPlaces: 3});
      const {value: yMax} = getUserUnits(configObj?.yAxis?.[0]?.max
         || yMinMax?.max, dataUnit, 'depth', {decimalPlaces: 3});
      setLabel(label);

      return {
        ...configObj,
        series: [{
          ...configObj?.series?.[0],
          data,
          tooltip: {
            valueDecimals: 2,
            valueSuffix: t(` ${label}`),
          },
          name: t('Rain Rate'),
        }],

        yAxis: [{
          ...(configObj?.yAxis?.[0] || {
            title: null,
            opposite: false,
            labels: {
              y: -1,
              x: 0,
              align: 'left'
            }
          }),
          min: yMin,
          max: yMax
        }],
      };
    }

    return configObj;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateYminmax]);

  return (
    <>
      <NewSensorDataChart
        sensorName={sensor.name}
        sensorId={sensor.id}
        sensorType={sensor.type}
        sensorIdentifier={sensor.identifier}
        variables={VAR_RAIN_RATE}
        startDate={startDate}
        endDate={endDate}
        cached={cached}
        customConfig={customConfig}
        header={{title: t('Rain Rate'), units: t(label), right: []}}
        uniformYAxis={uniformYAxis}
        yMinMax={yMinMax}
      />
    </>
  );
};

RainRateChart.defaultProps = {
  sensor: null,
  startDate: null,
  endDate: null,
  cached: null,
  updateYminmax: () => null,
  uniformYAxis: false,
  yMinMaxMap: null,
};

export default RainRateChart;
